/**
 * ProgressRing Component
 *
 * SVG-based circular progress indicator with animated states.
 */

import React from 'react';
import { motion } from 'framer-motion';

const ProgressRing = ({
  size = 120,
  strokeWidth = 3,
  progress = 0, // 0-100
  isConnecting = false,
  isProcessing = false,
  color = '#10a37f'
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Different animation for connecting vs processing
  const getAnimation = () => {
    if (isConnecting) {
      return {
        strokeDashoffset: [circumference, circumference * 0.25, circumference],
        rotate: [0, 180, 360],
        transition: {
          strokeDashoffset: { repeat: Infinity, duration: 1.5, ease: 'easeInOut' },
          rotate: { repeat: Infinity, duration: 2, ease: 'linear' }
        }
      };
    }
    if (isProcessing) {
      return {
        rotate: 360,
        transition: {
          rotate: { repeat: Infinity, duration: 1.5, ease: 'linear' }
        }
      };
    }
    return {
      strokeDashoffset,
      transition: { duration: 0.5, ease: 'easeOut' }
    };
  };

  return (
    <svg
      className="progress-ring"
      width={size}
      height={size}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none'
      }}
    >
      {/* Gradient definitions */}
      <defs>
        <linearGradient id="progress-gradient-green" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10a37f" />
          <stop offset="50%" stopColor="#4facfe" />
          <stop offset="100%" stopColor="#10a37f" />
        </linearGradient>
        <linearGradient id="progress-gradient-purple" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="50%" stopColor="#764ba2" />
          <stop offset="100%" stopColor="#667eea" />
        </linearGradient>
        <linearGradient id="progress-gradient-orange" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f39c12" />
          <stop offset="50%" stopColor="#e74c3c" />
          <stop offset="100%" stopColor="#f39c12" />
        </linearGradient>

        {/* Glow filter */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.1)"
        strokeWidth={strokeWidth}
      />

      {/* Animated progress ring */}
      {(isConnecting || isProcessing || progress > 0) && (
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={
            isConnecting
              ? 'url(#progress-gradient-purple)'
              : isProcessing
              ? 'url(#progress-gradient-orange)'
              : 'url(#progress-gradient-green)'
          }
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          filter="url(#glow)"
          style={{
            transformOrigin: 'center',
            transform: 'rotate(-90deg)'
          }}
          initial={{ strokeDashoffset: circumference }}
          animate={getAnimation()}
        />
      )}

      {/* Animated dots for connecting state */}
      {isConnecting && (
        <>
          {[0, 1, 2].map((i) => (
            <motion.circle
              key={i}
              cx={size / 2 + radius * Math.cos((Math.PI * 2 * i) / 3 - Math.PI / 2)}
              cy={size / 2 + radius * Math.sin((Math.PI * 2 * i) / 3 - Math.PI / 2)}
              r={4}
              fill="#9b59b6"
              initial={{ opacity: 0.3, scale: 0.8 }}
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8]
              }}
              transition={{
                repeat: Infinity,
                duration: 1,
                delay: i * 0.2,
                ease: 'easeInOut'
              }}
            />
          ))}
        </>
      )}
    </svg>
  );
};

export default ProgressRing;
